/**
 * The machinery shared by the venture dot canvases: device-pixel sizing that
 * follows the element, pointer tracking, a rAF loop that runs only while the
 * canvas is on screen, and a single still frame under prefers-reduced-motion.
 */
import { useEffect, useRef } from "react";

const prefersReducedMotion = () =>
	typeof window !== "undefined" &&
	Boolean(window.matchMedia) &&
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Canvas backing store at the device pixel ratio (capped at 2), drawn in CSS px.
const fitToElement = (canvas, ctx, s) => {
	const rect = canvas.getBoundingClientRect();
	s.dpr = Math.min(window.devicePixelRatio || 1, 2);
	s.w = rect.width;
	s.h = rect.height;
	canvas.width = rect.width * s.dpr;
	canvas.height = rect.height * s.dpr;
	ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
};

const trackPointer = (canvas, s) => {
	const onMove = (e) => {
		const rect = canvas.getBoundingClientRect();
		s.mx = e.clientX - rect.left;
		s.my = e.clientY - rect.top;
	};
	const onEnter = () => (s.hovering = true);
	const onLeave = () => (s.hovering = false);
	canvas.addEventListener("pointermove", onMove);
	canvas.addEventListener("pointerenter", onEnter);
	canvas.addEventListener("pointerleave", onLeave);
	return () => {
		canvas.removeEventListener("pointermove", onMove);
		canvas.removeEventListener("pointerenter", onEnter);
		canvas.removeEventListener("pointerleave", onLeave);
	};
};

// Runs the loop while the canvas intersects the viewport; returns a stop function.
const runWhileVisible = (canvas, s, frame, { reduced, eager }) => {
	let visible = eager;
	const loop = (t) => {
		frame(t, true);
		if (visible) s.raf = requestAnimationFrame(loop);
	};
	const stop = () => {
		if (s.raf) cancelAnimationFrame(s.raf);
		s.raf = 0;
	};
	const io = new IntersectionObserver(
		([entry]) => {
			if (entry.isIntersecting && !s.raf && !reduced) {
				visible = true;
				s.raf = requestAnimationFrame(loop);
			} else if (!entry.isIntersecting) {
				visible = false;
				s.hovering = false;
				stop();
			}
		},
		{ threshold: 0 },
	);
	io.observe(canvas);
	if (reduced) frame(0, false);
	else if (eager) s.raf = requestAnimationFrame(loop);
	return () => {
		stop();
		io.disconnect();
	};
};

/**
 * Animates a canvas of dots built once on mount.
 * @param {() => any[]} buildDots the dots, in unit space
 * @param {(ctx: CanvasRenderingContext2D, state: any, t: number, animate: boolean) => void} drawFrame
 * @param {{ eager?: boolean }} [options] eager starts the loop on mount instead
 *   of waiting for the first intersection report.
 * @returns {import("react").MutableRefObject<HTMLCanvasElement | null>} attach to the <canvas>
 */
export default function useDotCanvas(buildDots, drawFrame, { eager = false } = {}) {
	const canvasRef = useRef(null);
	const stateRef = useRef({
		dots: [],
		mx: 0,
		my: 0,
		hovering: false,
		raf: 0,
		dpr: 1,
		w: 0,
		h: 0,
	});

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!canvas || !ctx) return undefined;
		const s = stateRef.current;
		s.dots = buildDots();
		const resize = () => fitToElement(canvas, ctx, s);
		resize();
		const ro = new ResizeObserver(resize);
		ro.observe(canvas);
		const untrack = trackPointer(canvas, s);
		const frame = (t, animate) => drawFrame(ctx, s, t, animate);
		const stop = runWhileVisible(canvas, s, frame, { reduced: prefersReducedMotion(), eager });
		return () => {
			stop();
			ro.disconnect();
			untrack();
		};
		// buildDots and drawFrame are module-level functions; the canvas is built once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return canvasRef;
}
